// SPDX-License-Identifier: GPL-3.0-or-later
import type { ConnectionStatus, PeerInfo, RoomName } from '@ffb/shared';
import type { MediaService } from '../services/MediaService.js';
import type { AudioLevelService } from '../services/AudioLevelService.js';
import type { RemoteAudioSinkService } from '../services/RemoteAudioSinkService.js';
import type {
  DataHandle,
  OutgoingCallHandle,
  SignalingService,
} from '../services/SignalingService.js';

const POLL_MS = 2500;

export interface RoomViewModelOptions {
  userName: string;
  roomName: RoomName;
  media: MediaService;
  signaling: SignalingService;
  audioLevels: AudioLevelService;
  remoteAudio: RemoteAudioSinkService;
}

// MVVM: оркестрация комнаты. Держит реактивное состояние и сводит сервисы между собой.
// DOM-агностичен — всё, что видно в UI, живёт в $state и читается компонентами через биндинги.
export class RoomViewModel {
  readonly userName: string;
  readonly roomName: RoomName;

  status = $state<ConnectionStatus>('initializing');
  error = $state<string | null>(null);
  muted = $state(false);
  myLevel = $state(0);
  peers = $state<Record<string, PeerInfo>>({});
  copied = $state(false);

  private readonly media: MediaService;
  private readonly signaling: SignalingService;
  private readonly audioLevels: AudioLevelService;
  private readonly remoteAudio: RemoteAudioSinkService;
  private myPeerId: string | null = null;
  private outgoing = new Map<string, OutgoingCallHandle>();
  private nameAnnouncements = new Map<string, DataHandle>();
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private levelUnsubscribe: (() => void) | null = null;

  constructor(opts: RoomViewModelOptions) {
    this.userName = opts.userName;
    this.roomName = opts.roomName;
    this.media = opts.media;
    this.signaling = opts.signaling;
    this.audioLevels = opts.audioLevels;
    this.remoteAudio = opts.remoteAudio;
  }

  async start(): Promise<void> {
    try {
      this.status = 'requesting-mic';
      const stream = await this.media.acquire();
      this.levelUnsubscribe = this.audioLevels.attach('_me', stream, (lvl) => {
        this.myLevel = this.muted ? 0 : lvl;
      });

      this.signaling.onIncomingCall((call) => {
        const stream = this.media.getStream();
        if (!stream) return;
        call.answer(stream);
        const incomingName = readName(call.metadata);
        this.ensurePeer(call.from, incomingName);
        // В ответ открываем DataConnection с нашим именем — A узнает B.
        this.announceNameTo(call.from);
        call.onStream((remote) => this.attachRemoteStream(call.from, remote));
        call.onClose(() => this.dropPeer(call.from));
      });
      this.signaling.onIncomingData((ev) => {
        const name = readName(ev.metadata);
        if (name) this.updatePeerName(ev.from, name);
      });
      this.signaling.onError((err) => {
        this.error = err.message;
      });

      this.status = 'connecting';
      this.myPeerId = await this.signaling.open(this.roomName);

      await this.refreshPeers();
      this.pollTimer = setInterval(() => void this.refreshPeers(), POLL_MS);
      this.status = 'connected';
    } catch (err) {
      this.status = 'error';
      const name = (err as { name?: string } | null)?.name;
      this.error =
        name === 'NotAllowedError'
          ? 'Нет доступа к микрофону. Разрешите его в настройках и перезайдите.'
          : err instanceof Error
            ? err.message
            : 'Не удалось подключиться';
    }
  }

  toggleMute(): void {
    this.muted = !this.muted;
    this.media.setMuted(this.muted);
    if (this.muted) this.myLevel = 0;
  }

  async copyRoomName(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.roomName);
      this.copied = true;
      setTimeout(() => (this.copied = false), 2000);
    } catch {
      /* без clipboard — молча игнорируем, Copy UX не критичен */
    }
  }

  async refreshPeers(): Promise<void> {
    if (!this.myPeerId) return;
    try {
      const ids = await this.signaling.listPeers();
      const active = new Set(ids);
      const localStream = this.media.getStream();
      if (!localStream) return;

      // Для уникальности — звонит тот, у кого peerId «больше». Так избегаем двух встречных offers.
      for (const id of ids) {
        if (!this.outgoing.has(id) && this.myPeerId > id) {
          // Передаём своё имя в metadata — получатель увидит его сразу в mc.metadata.
          const handle = this.signaling.call(id, localStream, {
            metadata: { name: this.userName },
          });
          this.outgoing.set(id, handle);
          this.ensurePeer(id);
          handle.onStream((remote) => this.attachRemoteStream(id, remote));
          handle.onClose(() => this.dropPeer(id));
        }
      }

      // Удалить тех, кого больше нет в списке.
      for (const id of Object.keys(this.peers)) {
        if (!active.has(id)) this.dropPeer(id);
      }
    } catch (err) {
      console.warn('[RoomVM] refreshPeers failed', err);
    }
  }

  async leave(): Promise<void> {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    for (const handle of this.outgoing.values()) handle.close();
    this.outgoing.clear();
    for (const dc of this.nameAnnouncements.values()) dc.close();
    this.nameAnnouncements.clear();
    this.levelUnsubscribe?.();
    this.audioLevels.releaseAll();
    this.remoteAudio.stopAll();
    this.signaling.close();
    this.media.release();
  }

  private ensurePeer(id: string, name?: string): void {
    const existing = this.peers[id];
    if (existing) {
      // Обновим имя, если пришло и отличается от текущего.
      if (name && name !== existing.name) {
        this.peers = { ...this.peers, [id]: { ...existing, name } };
      }
      return;
    }
    this.peers = {
      ...this.peers,
      [id]: { id, name: name ?? '', connected: false, audioLevel: 0 },
    };
  }

  private updatePeerName(id: string, name: string): void {
    const existing = this.peers[id];
    if (!existing) {
      // Пир ещё не зарегистрирован (data-conn может обогнать call) — создаём запись с именем.
      this.ensurePeer(id, name);
      return;
    }
    if (existing.name === name) return;
    this.peers = { ...this.peers, [id]: { ...existing, name } };
  }

  private announceNameTo(id: string): void {
    if (this.nameAnnouncements.has(id)) return;
    try {
      const handle = this.signaling.openData(id, { name: this.userName });
      this.nameAnnouncements.set(id, handle);
    } catch (err) {
      console.warn('[RoomVM] announceNameTo failed', err);
    }
  }

  private attachRemoteStream(id: string, stream: MediaStream): void {
    const existing = this.peers[id];
    this.peers = {
      ...this.peers,
      [id]: { ...(existing ?? { id, name: '', audioLevel: 0 }), connected: true },
    };
    this.remoteAudio.play(id, stream);
    this.audioLevels.attach(id, stream, (lvl) => {
      const p = this.peers[id];
      if (!p) return;
      if (Math.abs((p.audioLevel ?? 0) - lvl) > 0.04) {
        this.peers = { ...this.peers, [id]: { ...p, audioLevel: lvl } };
      }
    });
  }

  private dropPeer(id: string): void {
    const handle = this.outgoing.get(id);
    handle?.close();
    this.outgoing.delete(id);
    const dc = this.nameAnnouncements.get(id);
    dc?.close();
    this.nameAnnouncements.delete(id);
    this.audioLevels.release(id);
    this.remoteAudio.stop(id);
    if (!this.peers[id]) return;
    const next = { ...this.peers };
    delete next[id];
    this.peers = next;
  }
}

function readName(metadata: Record<string, unknown> | null | undefined): string | undefined {
  const raw = metadata?.['name'];
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}
